import { FeatureFlag } from '@mews-distributor/common';
import { DistributorPromiseThunkAction } from '../../types';
import { getServiceEnterpriseId } from '../entities/services/selectors';
import { AllProductsDocument } from '../graphql/generatedClient';
import { fetchProductsFailed, fetchProductsStarted, fetchProductsSucceeded } from './reducer';
import { convertPricedProductsToProducts } from './utils';

export const fetchProducts =
    (serviceId: string): DistributorPromiseThunkAction<string> =>
    async (dispatch, getState, { apolloClient, featureFlagService, loggerService, repositories }) => {
        const state = getState();
        try {
            dispatch(fetchProductsStarted({ serviceId }));
            const enterpriseId = getServiceEnterpriseId(state, { serviceId });
            if (enterpriseId == null) {
                loggerService.log('missing-enterprise-id', "Couldn't find enterpriseId for service", { serviceId });
                throw Error("Couldn't find enterpriseId for service");
            }

            const useGraphQL = featureFlagService.hasFlag(FeatureFlag.bookingEngineUpsellEnabled);

            if (useGraphQL) {
                if (apolloClient == null) {
                    loggerService.log(
                        'apollo-client-unavailable',
                        'Apollo Client is not available, cannot fetch products via GraphQL',
                        {
                            serviceId,
                            enterpriseId,
                        }
                    );
                    throw Error('Apollo Client is not available');
                }

                const { data, error } = await apolloClient.query({
                    query: AllProductsDocument,
                    variables: {
                        enterpriseId,
                        serviceId,
                    },
                });

                if (error != null) {
                    loggerService.log('graphql-query-error', 'GraphQL query failed', {
                        serviceId,
                        enterpriseId,
                        errorMessage: error.message,
                        graphQLErrors: error.graphQLErrors,
                        networkError: error.networkError,
                    });
                    throw error;
                }

                if (data?.allProducts == null) {
                    loggerService.log('graphql-query-no-data', 'GraphQL query returned no products data', {
                        serviceId,
                        enterpriseId,
                        receivedData: data,
                    });
                    throw Error('GraphQL query returned no products data');
                }

                // Convert GraphQL PricedProduct format to REST Product format for backward compatibility
                const products = convertPricedProductsToProducts(data.allProducts);

                dispatch(fetchProductsSucceeded({ products, serviceId, enterpriseId }));
            } else {
                // REST path (original implementation)
                const { products } = await repositories.products.getAll({ serviceId });
                dispatch(fetchProductsSucceeded({ products, serviceId, enterpriseId }));
            }
        } catch (error) {
            dispatch(fetchProductsFailed({ serviceId }));
            loggerService.log('products-fetch-failed', 'Failed fetching products for service', { serviceId, error });
            throw error;
        }
        return serviceId;
    };
